// ─── Credential Collector Wizard (AC-10 to AC-23) ────────────────
// Two-level credential wizard: account-level → project-level.
// Platform-aware: shows only steps for the founder's chosen platforms.
// Progress persists via API — survives page refresh.
//
// Security: credentials are write-only (never returned after storage),
// never logged, AES-256-GCM encrypted, service role keys never sent to browser.

import React, { useState, useCallback, useEffect } from 'react';
import {
  Server, Globe, Database, CreditCard, Mail, ChevronRight,
  Check, AlertTriangle, Loader2, ExternalLink, SkipForward,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// ─── Types ──────────────────────────────────────────────────────

type Platform = 'railway' | 'render' | 'netlify' | 'vercel' | 'supabase' | 'neon';
type WizardMode = 'account' | 'project';
type Step =
  | 'platforms'
  | 'api_hosting'
  | 'frontend'
  | 'stripe'
  | 'mailgun'
  | 'api_service'
  | 'frontend_site'
  | 'database'
  | 'mailgun_domain'
  | 'summary';

interface StepStatus {
  completed: boolean;
  skipped: boolean;
  error?: string;
}

interface CredentialWizardProps {
  projectId?: string;
  mode?: WizardMode;
  onComplete: () => void;
  onBack?: () => void;
}

// ─── Platform Config ────────────────────────────────────────────

const PLATFORM_HELP: Record<string, { label: string; createUrl: string; createHint: string; tokenUrl: string; tokenHint: string; prefix?: string }> = {
  railway: {
    label: 'Railway',
    createUrl: 'https://railway.app',
    createHint: 'Railway is free to start — no credit card required.',
    tokenUrl: 'https://railway.app/account/tokens',
    tokenHint: 'Click "New Token" → name it "OCNexus" → set scope to "Full Access". Copy the token immediately — Railway won\'t show it again.',
  },
  render: {
    label: 'Render',
    createUrl: 'https://render.com',
    createHint: 'Render is free to start for individual developers.',
    tokenUrl: 'https://dashboard.render.com/u/settings#api-keys',
    tokenHint: 'Click "Create API Key" → name it "OCNexus". Copy the key immediately — Render won\'t show it again.',
  },
  netlify: {
    label: 'Netlify',
    createUrl: 'https://netlify.com',
    createHint: 'Netlify has a generous free tier for static sites and SPAs.',
    tokenUrl: 'https://app.netlify.com/user/applications#personal-access-tokens',
    tokenHint: 'Scroll to "Personal access tokens" → "New access token". Name it "OCNexus" → set expiration to "No expiration". Copy immediately.',
  },
  vercel: {
    label: 'Vercel',
    createUrl: 'https://vercel.com',
    createHint: 'Vercel has a free Hobby tier for individual developers.',
    tokenUrl: 'https://vercel.com/account/tokens',
    tokenHint: 'Click "Create" → name it "OCNexus" → set scope to your team. Set expiration to "No Expiration". Copy immediately.',
  },
};

// ─── Shared Styles ──────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: 16,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', fontSize: 'var(--text-sm)',
  fontFamily: 'var(--font-mono)', background: 'var(--surface3)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
  color: 'var(--text)', outline: 'none', boxSizing: 'border-box' as const,
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 20px', fontSize: 'var(--text-sm)', fontWeight: 600,
  background: 'var(--orange)', border: 'none', borderRadius: 'var(--radius-md)',
  color: 'white', cursor: 'pointer', fontFamily: 'var(--font-sans)',
  display: 'inline-flex', alignItems: 'center', gap: 6,
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary, background: 'transparent',
  border: '1px solid var(--border)', color: 'var(--text-muted)',
};

const hintStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 8,
  lineHeight: 1.5, fontFamily: 'var(--font-mono)',
};

// ─── Sub-components ─────────────────────────────────────────────

function StatusIcon({ ok, loading: isLoading }: { ok: boolean | null; loading?: boolean }) {
  if (isLoading) return <Loader2 size={14} style={{ color: 'var(--orange)', animation: 'spin 1s linear infinite' }} />;
  if (ok === true) return <Check size={14} style={{ color: 'var(--green)' }} />;
  if (ok === false) return <AlertTriangle size={14} style={{ color: 'var(--red)' }} />;
  return null;
}

function HelpLink({ url, label }: { url: string; label: string }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      style={{ color: 'var(--orange)', fontSize: 'var(--text-xs)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
      {label} <ExternalLink size={10} />
    </a>
  );
}

// ─── AC-10: Platform Selection ──────────────────────────────────

function PlatformSelectionStep({ onSave }: {
  onSave: (selections: { api: Platform; frontend: Platform; database: Platform }) => void;
}) {
  const [api, setApi] = useState<Platform | ''>('');
  const [frontend, setFrontend] = useState<Platform | ''>('');
  const [database, setDatabase] = useState<Platform | ''>('');

  const canContinue = api && frontend && database;

  function RadioGroup({ label, icon: Icon, options, value, onChange }: {
    label: string;
    icon: React.ElementType;
    options: Array<{ id: Platform; label: string }>;
    value: string;
    onChange: (v: Platform) => void;
  }) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>
          <Icon size={14} /> {label}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {options.map((opt: { id: Platform; label: string }) => (
            <button key={opt.id} onClick={() => onChange(opt.id)} style={{
              flex: 1, padding: '12px 16px', borderRadius: 'var(--radius-md)',
              border: `1px solid ${value === opt.id ? 'var(--orange)' : 'var(--border)'}`,
              background: value === opt.id ? 'var(--orange-dim, rgba(0,229,255,0.08))' : 'var(--surface3)',
              color: value === opt.id ? 'var(--orange)' : 'var(--text-muted)',
              cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 500,
              fontFamily: 'var(--font-sans)', transition: 'all 0.15s',
            }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 4 }}>Which platforms are you using?</h3>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 24 }}>
        We'll collect only the credentials needed for your stack.
      </p>

      <RadioGroup label="API hosting" icon={Server}
        options={[{ id: 'railway', label: 'Railway' }, { id: 'render', label: 'Render' }]}
        value={api} onChange={(v: Platform) => setApi(v)} />
      <RadioGroup label="Frontend hosting" icon={Globe}
        options={[{ id: 'netlify', label: 'Netlify' }, { id: 'vercel', label: 'Vercel' }]}
        value={frontend} onChange={(v: Platform) => setFrontend(v)} />
      <RadioGroup label="Database" icon={Database}
        options={[{ id: 'supabase', label: 'Supabase' }, { id: 'neon', label: 'Neon' }]}
        value={database} onChange={(v: Platform) => setDatabase(v)} />

      <button onClick={() => canContinue && onSave({ api: api as Platform, frontend: frontend as Platform, database: database as Platform })}
        disabled={!canContinue} style={{ ...btnPrimary, opacity: canContinue ? 1 : 0.5, width: '100%', justifyContent: 'center', marginTop: 8 }}>
        Continue <ChevronRight size={14} />
      </button>
    </div>
  );
}

// ─── AC-13/14: Token Input Step (Railway/Render/Netlify/Vercel) ─

function TokenInputStep({ platform, onVerified, onSkip }: {
  platform: Platform;
  onVerified: () => void;
  onSkip?: () => void;
}) {
  const { subscriber } = useAuth();
  const [value, setValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<{ valid: boolean; message: string } | null>(null);

  const help = PLATFORM_HELP[platform];
  if (!help) return null;

  async function verify() {
    if (!value.trim() || !subscriber) return;
    setVerifying(true);
    setResult(null);
    try {
      // Save credential first (write-only — never returned after)
      await api.saveWorkspaceCredential(subscriber.id, platform, value.trim());
      // Then validate
      const res = await api.validateProvider(platform, { token: value.trim() });
      if (res.valid) {
        setResult({ valid: true, message: `Connected to ${help.label}` });
        setTimeout(onVerified, 800);
      } else {
        setResult({ valid: false, message: res.errors?.[0] || 'Verification failed' });
      }
    } catch (err) {
      setResult({ valid: false, message: err instanceof Error ? err.message : 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 4 }}>Connect {help.label}</h3>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, display: 'block' }}>
          {platform === 'render' ? 'API Key' : 'Personal Access Token'}
        </label>
        <input type="password" autoComplete="off" value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          placeholder={`Paste your ${help.label} token`}
          style={inputStyle} />
        <div style={hintStyle}>
          <HelpLink url={help.tokenUrl} label={`Create your token at ${help.label}`} />
          <br />{help.tokenHint}
        </div>
      </div>

      {result && (
        <div style={{
          padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12,
          background: result.valid ? 'var(--green-dim, rgba(34,197,94,0.08))' : 'var(--red-dim, rgba(239,68,68,0.08))',
          border: `1px solid ${result.valid ? 'var(--green)' : 'var(--red)'}`,
          fontSize: 'var(--text-xs)', color: result.valid ? 'var(--green)' : 'var(--red)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <StatusIcon ok={result.valid} /> {result.message}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={verify} disabled={!value.trim() || verifying}
          style={{ ...btnPrimary, opacity: !value.trim() || verifying ? 0.5 : 1, flex: 1, justifyContent: 'center' }}>
          {verifying ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Verifying...</> : 'Verify connection'}
        </button>
        {onSkip && (
          <button onClick={onSkip} style={btnSecondary}>
            <SkipForward size={12} /> Skip
          </button>
        )}
      </div>
    </div>
  );
}

// ─── AC-15: Stripe UI ───────────────────────────────────────────

function StripeStep({ onVerified }: { onVerified: () => void }) {
  const { subscriber } = useAuth();
  const [pk, setPk] = useState('');
  const [sk, setSk] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<{ valid: boolean; message: string } | null>(null);

  const pkValid = pk.startsWith('pk_test_') || pk.startsWith('pk_live_');
  const skValid = sk.startsWith('sk_test_') || sk.startsWith('sk_live_');
  const envMismatch = pkValid && skValid && pk.split('_')[1] !== sk.split('_')[1];

  async function verify() {
    if (!pkValid || !skValid || !subscriber) return;
    setVerifying(true);
    setResult(null);
    try {
      await api.saveWorkspaceCredentialKeyed(subscriber.id, 'stripe', 'publishable_key', pk.trim());
      await api.saveWorkspaceCredentialKeyed(subscriber.id, 'stripe', 'secret_key', sk.trim());
      const res = await api.validateProvider('stripe', { publishable_key: pk.trim(), secret_key: sk.trim() });
      if (res.valid) {
        setResult({ valid: true, message: 'Stripe connected' });
        setTimeout(onVerified, 800);
      } else {
        setResult({ valid: false, message: res.errors?.[0] || 'Verification failed' });
      }
    } catch (err) {
      setResult({ valid: false, message: err instanceof Error ? err.message : 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 4 }}>Connect Stripe</h3>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 16 }}>
        Stripe handles your billing. OCNexus configures webhook endpoints per project.
      </p>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Publishable Key (starts with pk_)</label>
        <input type="password" autoComplete="off" value={pk}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPk(e.target.value)}
          placeholder="pk_test_..." style={inputStyle} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Secret Key (starts with sk_)</label>
        <input type="password" autoComplete="off" value={sk}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSk(e.target.value)}
          placeholder="sk_test_..." style={inputStyle} />
      </div>

      {envMismatch && (
        <div style={{ padding: '8px 12px', background: 'var(--yellow-dim)', border: '1px solid var(--yellow)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', color: 'var(--yellow)', marginBottom: 12 }}>
          Keys must be from the same environment (both test or both live).
        </div>
      )}

      <div style={hintStyle}>
        <HelpLink url="https://dashboard.stripe.com/apikeys" label="Find your keys at Stripe" />
        <br />Use Test keys while building, Live keys when ready for real payments.
      </div>

      {result && (
        <div style={{
          padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 12,
          background: result.valid ? 'var(--green-dim, rgba(34,197,94,0.08))' : 'var(--red-dim, rgba(239,68,68,0.08))',
          border: `1px solid ${result.valid ? 'var(--green)' : 'var(--red)'}`,
          fontSize: 'var(--text-xs)', color: result.valid ? 'var(--green)' : 'var(--red)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <StatusIcon ok={result.valid} /> {result.message}
        </div>
      )}

      <button onClick={verify} disabled={!pkValid || !skValid || envMismatch || verifying}
        style={{ ...btnPrimary, width: '100%', justifyContent: 'center', marginTop: 16, opacity: (!pkValid || !skValid || envMismatch || verifying) ? 0.5 : 1 }}>
        {verifying ? 'Verifying...' : 'Verify'}
      </button>
    </div>
  );
}

// ─── AC-16: Mailgun UI ──────────────────────────────────────────

function MailgunStep({ onVerified, onSkip }: { onVerified: () => void; onSkip: () => void }) {
  return <TokenInputStep platform={'mailgun' as Platform} onVerified={onVerified} onSkip={onSkip} />;
}

// ─── AC-17: API Service Selector ────────────────────────────────

function ServiceSelectorStep({ platform, projectId, onSelected }: {
  platform: 'railway' | 'render';
  projectId: string;
  onSelected: (serviceId: string) => void;
}) {
  const { subscriber } = useAuth();
  const [services, setServices] = useState<Array<{ id: string; name: string; project?: string; type?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (!subscriber) return;
    setLoading(true);
    api.listProviderServices(subscriber.id, platform).then((data: { services?: Array<{ id: string; name: string; project?: string; type?: string }> }) => {
      setServices(data.services || []);
      setLoading(false);
    }).catch((err: Error) => {
      setError(err.message);
      setLoading(false);
    });
  }, [subscriber, platform]);

  async function handleSelect() {
    if (!selected) return;
    // Store the service ID as a project credential
    await api.saveProjectCredential(projectId, platform, 'service_id', selected);
    onSelected(selected);
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 4 }}>
        Which {PLATFORM_HELP[platform]?.label} service is this project's API?
      </h3>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading services...
        </div>
      ) : error ? (
        <div style={{ padding: '8px 12px', background: 'var(--red-dim)', border: '1px solid var(--red)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', color: 'var(--red)' }}>
          {error}
        </div>
      ) : services.length === 0 ? (
        <div style={{ padding: '8px 12px', background: 'var(--yellow-dim)', border: '1px solid var(--yellow)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', color: 'var(--yellow)' }}>
          No services found. Deploy a service first, then return here.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {services.map((svc: { id: string; name: string; project?: string; type?: string }) => (
            <button key={svc.id} onClick={() => setSelected(svc.id)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderRadius: 'var(--radius-md)',
              border: `1px solid ${selected === svc.id ? 'var(--orange)' : 'var(--border)'}`,
              background: selected === svc.id ? 'var(--orange-dim, rgba(0,229,255,0.08))' : 'var(--surface3)',
              cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text)',
              textAlign: 'left' as const,
            }}>
              <span>{svc.name} {svc.project ? <span style={{ color: 'var(--text-muted)' }}>({svc.project})</span> : null}</span>
              {selected === svc.id && <Check size={14} style={{ color: 'var(--orange)' }} />}
            </button>
          ))}
        </div>
      )}

      <button onClick={handleSelect} disabled={!selected}
        style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: selected ? 1 : 0.5 }}>
        Select <ChevronRight size={14} />
      </button>
    </div>
  );
}

// ─── AC-18: Frontend Site Selector ──────────────────────────────

function SiteSelectorStep({ platform, projectId, onSelected }: {
  platform: 'netlify' | 'vercel';
  projectId: string;
  onSelected: (siteId: string) => void;
}) {
  const { subscriber } = useAuth();
  const [items, setItems] = useState<Array<{ id: string; name: string; url: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (!subscriber) return;
    setLoading(true);
    api.listProviderServices(subscriber.id, platform).then((data: { sites?: Array<{ id: string; name: string; url: string }>; projects?: Array<{ id: string; name: string; url?: string }> }) => {
      setItems(data.sites || data.projects?.map((p: { id: string; name: string; url?: string }) => ({ ...p, url: p.url || '' })) || []);
      setLoading(false);
    }).catch((err: Error) => {
      setError(err.message);
      setLoading(false);
    });
  }, [subscriber, platform]);

  async function handleSelect() {
    if (!selected) return;
    await api.saveProjectCredential(projectId, platform, 'site_id', selected);
    onSelected(selected);
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 4 }}>
        Which {PLATFORM_HELP[platform]?.label} {platform === 'netlify' ? 'site' : 'project'} is this project's frontend?
      </h3>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading {platform === 'netlify' ? 'sites' : 'projects'}...
        </div>
      ) : error ? (
        <div style={{ padding: '8px 12px', background: 'var(--red-dim)', border: '1px solid var(--red)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', color: 'var(--red)' }}>
          {error}
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: '8px 12px', background: 'var(--yellow-dim)', border: '1px solid var(--yellow)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', color: 'var(--yellow)' }}>
          No {platform === 'netlify' ? 'sites' : 'projects'} found. Create one first, then return here.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {items.map((item: { id: string; name: string; url: string }) => (
            <button key={item.id} onClick={() => setSelected(item.id)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderRadius: 'var(--radius-md)',
              border: `1px solid ${selected === item.id ? 'var(--orange)' : 'var(--border)'}`,
              background: selected === item.id ? 'var(--orange-dim, rgba(0,229,255,0.08))' : 'var(--surface3)',
              cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text)',
              textAlign: 'left' as const,
            }}>
              <div>
                <div>{item.name}</div>
                {item.url && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{item.url}</div>}
              </div>
              {selected === item.id && <Check size={14} style={{ color: 'var(--orange)' }} />}
            </button>
          ))}
        </div>
      )}

      <button onClick={handleSelect} disabled={!selected}
        style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: selected ? 1 : 0.5 }}>
        Select <ChevronRight size={14} />
      </button>
    </div>
  );
}

// ─── AC-19: Database UI ─────────────────────────────────────────

function DatabaseStep({ platform, projectId, onVerified }: {
  platform: 'supabase' | 'neon';
  projectId: string;
  onVerified: () => void;
}) {
  const [url, setUrl] = useState('');
  const [anonKey, setAnonKey] = useState('');
  const [serviceKey, setServiceKey] = useState('');
  const [connString, setConnString] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<{ valid: boolean; message: string } | null>(null);

  const portError = platform === 'supabase' && url.includes(':6543');

  async function verify() {
    setVerifying(true);
    setResult(null);
    try {
      if (platform === 'supabase') {
        await api.saveProjectCredential(projectId, 'supabase', 'project_url', url.trim());
        await api.saveProjectCredential(projectId, 'supabase', 'anon_key', anonKey.trim());
        await api.saveProjectCredential(projectId, 'supabase', 'service_role_key', serviceKey.trim());
        const res = await api.validateProvider('supabase', { url: url.trim(), anon_key: anonKey.trim(), service_role_key: serviceKey.trim() });
        if (res.valid) {
          setResult({ valid: true, message: 'Supabase connected' });
          setTimeout(onVerified, 800);
        } else {
          setResult({ valid: false, message: res.errors?.[0] || 'Verification failed' });
        }
      } else {
        await api.saveProjectCredential(projectId, 'neon', 'connection_string', connString.trim());
        const res = await api.validateProvider('neon', { connection_string: connString.trim() });
        if (res.valid) {
          setResult({ valid: true, message: 'Neon connected' });
          setTimeout(onVerified, 800);
        } else {
          setResult({ valid: false, message: res.errors?.[0] || 'Verification failed' });
        }
      }
    } catch (err) {
      setResult({ valid: false, message: err instanceof Error ? err.message : 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  }

  const canVerify = platform === 'supabase'
    ? url.trim() && anonKey.trim() && serviceKey.trim() && !portError
    : connString.trim();

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 4 }}>
        Connect {platform === 'supabase' ? 'Supabase' : 'Neon'}
      </h3>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 16 }}>
        Each project has its own database.
      </p>

      {platform === 'supabase' ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Project URL</label>
            <input type="text" value={url} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
              placeholder="https://xxxx.supabase.co" style={inputStyle} />
            {portError && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)', marginTop: 4 }}>Use port 5432 — never 6543.</div>}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Anon Key (publishable — safe in browser)</label>
            <input type="password" autoComplete="off" value={anonKey} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAnonKey(e.target.value)}
              placeholder="eyJ..." style={inputStyle} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Service Role Key (secret — server-side only)</label>
            <input type="password" autoComplete="off" value={serviceKey} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceKey(e.target.value)}
              placeholder="eyJ..." style={inputStyle} />
          </div>
          <div style={hintStyle}>
            <HelpLink url="https://app.supabase.com/project/_/settings/api" label="Find these at Supabase" />
          </div>
        </>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Connection String</label>
          <input type="password" autoComplete="off" value={connString} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConnString(e.target.value)}
            placeholder="postgresql://user:pass@xxxx.neon.tech/dbname" style={inputStyle} />
          <div style={hintStyle}>
            <HelpLink url="https://console.neon.tech/app/projects" label="Find this at Neon" />
            <br />Copy the non-pooled connection string for migrations.
          </div>
        </div>
      )}

      {result && (
        <div style={{
          padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12,
          background: result.valid ? 'var(--green-dim, rgba(34,197,94,0.08))' : 'var(--red-dim, rgba(239,68,68,0.08))',
          border: `1px solid ${result.valid ? 'var(--green)' : 'var(--red)'}`,
          fontSize: 'var(--text-xs)', color: result.valid ? 'var(--green)' : 'var(--red)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <StatusIcon ok={result.valid} /> {result.message}
        </div>
      )}

      <button onClick={verify} disabled={!canVerify || verifying}
        style={{ ...btnPrimary, width: '100%', justifyContent: 'center', marginTop: 8, opacity: (!canVerify || verifying) ? 0.5 : 1 }}>
        {verifying ? 'Verifying...' : 'Verify connection'}
      </button>
    </div>
  );
}

// ─── AC-20: Mailgun Domain UI ───────────────────────────────────

function MailgunDomainStep({ projectId, onVerified, onSkip }: {
  projectId: string;
  onVerified: () => void;
  onSkip: () => void;
}) {
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!domain.trim()) return;
    setSaving(true);
    try {
      await api.saveProjectCredential(projectId, 'mailgun_domain', 'domain', domain.trim());
      onVerified();
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 4 }}>Email domain for this project</h3>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 16 }}>
        Verify your domain in Mailgun first, then enter it here.
      </p>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Sending domain</label>
        <input type="text" value={domain} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDomain(e.target.value)}
          placeholder="mail.yourapp.com" style={inputStyle} />
        <div style={hintStyle}>
          <HelpLink url="https://app.mailgun.com/sending/domains" label="Add and verify your domain at Mailgun" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={!domain.trim() || saving}
          style={{ ...btnPrimary, flex: 1, justifyContent: 'center', opacity: (!domain.trim() || saving) ? 0.5 : 1 }}>
          Save domain
        </button>
        <button onClick={onSkip} style={btnSecondary}><SkipForward size={12} /> Skip</button>
      </div>
    </div>
  );
}

// ─── AC-21: Validation Summary ──────────────────────────────────

function ValidationSummary({ steps, onFinish, onFix }: {
  steps: Record<string, StepStatus>;
  onFinish: () => void;
  onFix: (step: Step) => void;
}) {
  const services = Object.entries(steps);
  const requiredFailed = services.some(([k, s]: [string, StepStatus]) =>
    !s.completed && !s.skipped && !['mailgun', 'mailgun_domain'].includes(k));

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: 16 }}>Setup Summary</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {services.map(([key, status]: [string, StepStatus]) => (
          <div key={key} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
            background: 'var(--surface3)', borderRadius: 'var(--radius-md)',
          }}>
            {status.completed ? <Check size={14} style={{ color: 'var(--green)' }} />
              : status.skipped ? <SkipForward size={14} style={{ color: 'var(--text-muted)' }} />
              : <AlertTriangle size={14} style={{ color: 'var(--yellow)' }} />}
            <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{key.replace(/_/g, ' ')}</span>
            {!status.completed && !status.skipped && (
              <button onClick={() => onFix(key as Step)} style={{
                background: 'none', border: 'none', color: 'var(--orange)', fontSize: 'var(--text-xs)',
                cursor: 'pointer', fontFamily: 'var(--font-mono)',
              }}>Fix</button>
            )}
          </div>
        ))}
      </div>
      <button onClick={onFinish} disabled={requiredFailed}
        style={{ ...btnPrimary, width: '100%', justifyContent: 'center', marginTop: 20, opacity: requiredFailed ? 0.5 : 1 }}>
        {requiredFailed ? 'Fix required services to continue' : 'Start using OCNexus'}
      </button>
    </div>
  );
}

// ─── Main Wizard ────────────────────────────────────────────────

export function CredentialWizard({ projectId, mode = 'account', onComplete, onBack }: CredentialWizardProps) {
  const { subscriber } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>(mode === 'project' ? 'api_service' : 'platforms');
  const [platforms, setPlatforms] = useState<{ api: Platform; frontend: Platform; database: Platform } | null>(null);
  const [stepStatus, setStepStatus] = useState<Record<string, StepStatus>>({});
  const [hasMailgunAccount, setHasMailgunAccount] = useState(false);

  // AC-22: Check if account creds already exist (subsequent project)
  const [accountCredsLoaded, setAccountCredsLoaded] = useState(false);
  const [existingAccountCreds, setExistingAccountCreds] = useState<string[]>([]);

  useEffect(() => {
    if (!subscriber) return;
    api.getCredentialStatus(subscriber.id).then((services: Array<{ service: string; connected: boolean }>) => {
      const connected = services.filter((s: { service: string; connected: boolean }) => s.connected).map((s: { service: string }) => s.service);
      setExistingAccountCreds(connected);
      setHasMailgunAccount(connected.includes('mailgun'));
      setAccountCredsLoaded(true);
    }).catch(() => setAccountCredsLoaded(true));
  }, [subscriber]);

  const markComplete = useCallback((step: string) => {
    setStepStatus((prev: Record<string, StepStatus>) => ({ ...prev, [step]: { completed: true, skipped: false } }));
  }, []);

  const markSkipped = useCallback((step: string) => {
    setStepStatus((prev: Record<string, StepStatus>) => ({ ...prev, [step]: { completed: false, skipped: true } }));
  }, []);

  // Determine which steps to show based on mode and existing creds
  const getAccountSteps = useCallback((): Step[] => {
    if (!platforms) return ['platforms'];
    const steps: Step[] = [];
    if (!existingAccountCreds.includes(platforms.api)) steps.push('api_hosting');
    if (!existingAccountCreds.includes(platforms.frontend)) steps.push('frontend');
    if (!existingAccountCreds.includes('stripe')) steps.push('stripe');
    steps.push('mailgun'); // always show (skippable)
    return steps;
  }, [platforms, existingAccountCreds]);

  const getProjectSteps = useCallback((): Step[] => {
    const steps: Step[] = ['api_service', 'frontend_site', 'database'];
    if (hasMailgunAccount) steps.push('mailgun_domain');
    return steps;
  }, [hasMailgunAccount]);

  function advanceToNext() {
    const allSteps: Step[] = mode === 'project'
      ? [...getProjectSteps(), 'summary']
      : [...getAccountSteps(), ...getProjectSteps(), 'summary'];

    const currentIdx = allSteps.indexOf(currentStep);
    if (currentIdx < allSteps.length - 1) {
      setCurrentStep(allSteps[currentIdx + 1]);
    }
  }

  function handlePlatformSave(selections: { api: Platform; frontend: Platform; database: Platform }) {
    setPlatforms(selections);
    if (subscriber) {
      api.savePlatformSelections(subscriber.id, {
        platform_api_hosting: selections.api,
        platform_frontend: selections.frontend,
        platform_database: selections.database,
      }).catch(() => {});
    }
    // Advance to first account step
    const firstStep = existingAccountCreds.includes(selections.api) ? 'frontend' : 'api_hosting';
    if (existingAccountCreds.includes(selections.api) && existingAccountCreds.includes(selections.frontend) && existingAccountCreds.includes('stripe')) {
      // AC-22: All account creds exist — skip to project steps
      setCurrentStep('database');
    } else {
      setCurrentStep(firstStep as Step);
    }
  }

  if (!accountCredsLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Loader2 size={20} style={{ color: 'var(--orange)', animation: 'spin 1s linear infinite' }} />
        <div style={{ marginTop: 8, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Loading...</div>
      </div>
    );
  }

  // AC-22: Header for subsequent projects
  const showAccountConnectedBanner = mode === 'project' && existingAccountCreds.length > 0;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: 4 }}>
          {mode === 'project' ? 'Connect project services' : 'Connect your infrastructure'}
        </h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {mode === 'project'
            ? `Wire ${projectId || 'this project'} to your services.`
            : 'Set up once — shared across all your projects.'}
        </p>
      </div>

      {/* AC-22: Account connected banner */}
      {showAccountConnectedBanner && (
        <div style={{
          padding: '10px 14px', background: 'var(--green-dim, rgba(34,197,94,0.08))',
          border: '1px solid var(--green)', borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-xs)', color: 'var(--green)', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Check size={14} />
          Account credentials connected ({existingAccountCreds.join(', ')})
        </div>
      )}

      {/* Step rendering */}
      {currentStep === 'platforms' && <PlatformSelectionStep onSave={handlePlatformSave} />}
      {currentStep === 'api_hosting' && platforms && (
        <TokenInputStep platform={platforms.api} onVerified={() => { markComplete('api_hosting'); advanceToNext(); }} />
      )}
      {currentStep === 'frontend' && platforms && (
        <TokenInputStep platform={platforms.frontend} onVerified={() => { markComplete('frontend'); advanceToNext(); }} />
      )}
      {currentStep === 'stripe' && (
        <StripeStep onVerified={() => { markComplete('stripe'); advanceToNext(); }} />
      )}
      {currentStep === 'mailgun' && (
        <MailgunStep
          onVerified={() => { markComplete('mailgun'); setHasMailgunAccount(true); advanceToNext(); }}
          onSkip={() => { markSkipped('mailgun'); advanceToNext(); }}
        />
      )}
      {currentStep === 'api_service' && platforms && projectId && (
        <ServiceSelectorStep platform={platforms.api as 'railway' | 'render'} projectId={projectId}
          onSelected={() => { markComplete('api_service'); advanceToNext(); }} />
      )}
      {currentStep === 'frontend_site' && platforms && projectId && (
        <SiteSelectorStep platform={platforms.frontend as 'netlify' | 'vercel'} projectId={projectId}
          onSelected={() => { markComplete('frontend_site'); advanceToNext(); }} />
      )}
      {currentStep === 'database' && platforms && projectId && (
        <DatabaseStep platform={platforms.database as 'supabase' | 'neon'} projectId={projectId}
          onVerified={() => { markComplete('database'); advanceToNext(); }} />
      )}
      {currentStep === 'mailgun_domain' && projectId && (
        <MailgunDomainStep projectId={projectId}
          onVerified={() => { markComplete('mailgun_domain'); advanceToNext(); }}
          onSkip={() => { markSkipped('mailgun_domain'); advanceToNext(); }} />
      )}
      {currentStep === 'summary' && (
        <ValidationSummary steps={stepStatus} onFinish={onComplete}
          onFix={(step: Step) => setCurrentStep(step)} />
      )}

      {/* Back button */}
      {onBack && currentStep === 'platforms' && (
        <button onClick={onBack} style={{ ...btnSecondary, marginTop: 8 }}>← Back</button>
      )}
    </div>
  );
}
